import { Button } from "@acme/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@acme/ui/dialog";
import { Input } from "@acme/ui/input";
import { PlusCircle, X } from "lucide-react";
import { useState } from "react";

import { type Command, useWebContainer } from "@/context/WebContainerContext";
import { Label } from "@acme/ui/label";
import { createId } from "@paralleldrive/cuid2";
import { useForm } from "@tanstack/react-form";
import { LuChevronDown } from "react-icons/lu";
import * as z from "zod";

const formSchema = z.object({
	name: z.string().min(1, "Name is required"),
	command: z.string().min(1, "Command is required"),
});

export default function CustomCommandButton() {
	const { setCommand, getCommands } = useWebContainer();
	const [commands, setCommands] = useState<Command[]>(getCommands());
	const [open, setOpen] = useState(false);

	const form = useForm({
		defaultValues: {
			name: "",
			command: "",
		},
		onSubmit: async ({ value }) => {
			// Add command to the state
			setCommands([
				...commands,
				{
					name: value.name,
					command: value.command.split(" ")[0] ?? "",
					args: value.command.split(" ").slice(1),
					id: createId(),
				},
			]);
		},
	});

	function removeCommand(id: string) {
		setCommands(commands.filter((command) => command.id !== id));
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="ghost" size="icon">
					<LuChevronDown />
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px] bg-card border-none">
				<DialogHeader>
					<DialogTitle>Custom Commands</DialogTitle>
					<DialogDescription>
						Add or remove custom commands. Click save when you're done.
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4"
					>
						<form.Field name="name">
							{(field) => (
								<>
									<Label>Name</Label>
									<Input
										placeholder="Enter command name"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
									/>
									{field.state.meta.errors && (
										<p className="text-red-500 text-sm mt-1">
											{field.state.meta.errors}
										</p>
									)}
								</>
							)}
						</form.Field>
						<form.Field name="command">
							{(field) => (
								<>
									<Label>Command</Label>
									<Input
										placeholder="Enter command"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
									/>
									{field.state.meta.errors && (
										<p className="text-red-500 text-sm mt-1">
											{field.state.meta.errors}
										</p>
									)}
								</>
							)}
						</form.Field>
						<Button type="submit" variant="link">
							<PlusCircle className="h-4 w-4" />
							Add Command
						</Button>
					</form>

					<div className="mt-4">
						<h4 className="mb-2 font-medium">Existing Commands</h4>
						{commands.map((command) => (
							<div
								key={command.id}
								className="flex items-center justify-between py-2"
							>
								<span>
									{command.name}: {[command.command, ...command.args].join(" ")}
								</span>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => removeCommand(command.id)}
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						))}
					</div>
				</div>
				<Button
					onClick={() => {
						for (const command of commands) {
							setCommand(command, command.id);
						}
						setOpen(false);
					}}
				>
					Save
				</Button>
			</DialogContent>
		</Dialog>
	);
}
